package top.wcpe.allincore.ui.web.mock

internal enum class MockTerminalKind {
    SUCCESS,
    ERROR,
    TIMEOUT,
    CANCELLED,
    UNREGISTERED,
}

internal data class MockTrace(
    val sessionId: Long,
    val requestId: String,
    val path: String,
    val requestPayload: Map<String, Any?>,
    val match: MockMatchLocation,
    val delayMs: Long,
    val result: MockTerminalKind,
    val resultPayload: Map<String, Any?>,
)

internal interface MockTraceSink {
    fun record(trace: MockTrace)

    fun clearSession(sessionId: Long)

    fun clearAll()
}

internal class InMemoryMockTraceSink(
    maxEntries: Int = DEFAULT_TRACE_MAX_ENTRIES,
    maxBytes: Long = DEFAULT_TRACE_MAX_BYTES,
) : MockTraceSink {
    private val ledger = MockTerminalLedger(maxEntries, maxBytes)

    override fun record(trace: MockTrace) {
        ledger.append(trace, callbackInProgress = false)
    }

    override fun clearSession(sessionId: Long) {
        ledger.clearSession(sessionId)
    }

    override fun clearAll() {
        ledger.clearAll()
    }

    fun find(sessionId: Long, requestId: String): MockTrace? = ledger.find(sessionId, requestId)

    fun records(): List<MockTrace> = ledger.records
}

internal data class MockDiagnostic(
    val code: String,
    val message: String,
    val sessionId: Long? = null,
    val requestId: String? = null,
    val path: String? = null,
)

internal fun interface MockDiagnosticSink {
    fun report(diagnostic: MockDiagnostic)
}

internal object ConsoleMockDiagnosticSink : MockDiagnosticSink {
    override fun report(diagnostic: MockDiagnostic) {
        val context = buildList {
            diagnostic.sessionId?.let { add("sessionId=$it") }
            diagnostic.requestId?.let { add("requestId=$it") }
            diagnostic.path?.let { add("path=$it") }
        }.joinToString(", ")
        val suffix = if (context.isEmpty()) "" else "（$context）"
        console.warn("${diagnostic.code}：${diagnostic.message}$suffix")
    }
}

internal sealed interface MockLedgerAppendResult {
    data class Stored(val trace: MockTrace) : MockLedgerAppendResult

    data object TemporarilyFull : MockLedgerAppendResult

    data object Oversized : MockLedgerAppendResult
}

internal class MockTerminalLedger(
    private val maxEntries: Int = DEFAULT_TRACE_MAX_ENTRIES,
    private val maxBytes: Long = DEFAULT_TRACE_MAX_BYTES,
) {
    private data class TraceKey(val sessionId: Long, val requestId: String)

    private data class Entry(val trace: MockTrace, val bytes: Long, var callbackInProgress: Boolean)

    private val entries = linkedMapOf<TraceKey, Entry>()
    private val sessionsPendingClear = mutableSetOf<Long>()
    private var clearAllPending = false
    private var totalBytes = 0L

    init {
        require(maxEntries > 0) { "Mock 轨迹容量必须大于零" }
        require(maxBytes > 0L) { "Mock 轨迹字节上限必须大于零" }
    }

    fun append(trace: MockTrace, callbackInProgress: Boolean): MockTrace? =
        when (val result = tryAppend(trace, callbackInProgress)) {
            is MockLedgerAppendResult.Stored -> result.trace
            MockLedgerAppendResult.Oversized,
            MockLedgerAppendResult.TemporarilyFull,
            -> null
        }

    fun tryAppend(trace: MockTrace, callbackInProgress: Boolean): MockLedgerAppendResult {
        val key = TraceKey(trace.sessionId, trace.requestId)
        entries[key]?.let { return MockLedgerAppendResult.Stored(snapshotTrace(it.trace)) }
        val snapshot = fitPayload(snapshotTrace(trace))
        val bytes = traceByteSize(snapshot)
        if (bytes > maxBytes) return MockLedgerAppendResult.Oversized
        if (!evictUntilFits(bytes)) return MockLedgerAppendResult.TemporarilyFull
        entries[key] = Entry(snapshot, bytes, callbackInProgress)
        totalBytes += bytes
        if (!callbackInProgress) release(snapshot.sessionId, snapshot.requestId)
        return MockLedgerAppendResult.Stored(snapshotTrace(snapshot))
    }

    fun release(sessionId: Long, requestId: String) {
        val key = TraceKey(sessionId, requestId)
        val entry = entries[key] ?: return
        entry.callbackInProgress = false
        if (clearAllPending || sessionId in sessionsPendingClear) {
            remove(key)
            finishPendingClears()
        } else {
            trimToLimits()
        }
    }

    fun find(sessionId: Long, requestId: String): MockTrace? =
        entries[TraceKey(sessionId, requestId)]?.trace?.let(::snapshotTrace)

    val records: List<MockTrace>
        get() = entries.values.map { snapshotTrace(it.trace) }

    fun clearSession(sessionId: Long) {
        sessionsPendingClear += sessionId
        removeWhere { key, entry -> key.sessionId == sessionId && !entry.callbackInProgress }
        finishPendingClears()
    }

    fun clearAll() {
        clearAllPending = true
        removeWhere { _, entry -> !entry.callbackInProgress }
        finishPendingClears()
    }

    private fun fitPayload(trace: MockTrace): MockTrace {
        if (traceByteSize(trace) <= maxBytes) return trace
        return trace.copy(
            requestPayload = PAYLOAD_DROPPED_MARKER,
            resultPayload = PAYLOAD_DROPPED_MARKER,
        )
    }

    private fun evictUntilFits(incomingBytes: Long): Boolean {
        while (entries.size >= maxEntries || totalBytes + incomingBytes > maxBytes) {
            val key = entries.entries.firstOrNull { !it.value.callbackInProgress }?.key ?: return false
            remove(key)
        }
        return true
    }

    private fun trimToLimits() {
        while (entries.size > maxEntries || totalBytes > maxBytes) {
            val key = entries.entries.firstOrNull { !it.value.callbackInProgress }?.key ?: return
            remove(key)
        }
    }

    private fun finishPendingClears() {
        if (entries.isEmpty()) clearAllPending = false
        sessionsPendingClear.removeAll { sessionId ->
            entries.keys.none { key -> key.sessionId == sessionId }
        }
    }

    private fun removeWhere(predicate: (TraceKey, Entry) -> Boolean) {
        val keys = entries.keys.filter { key ->
            entries[key]?.let { entry -> predicate(key, entry) } == true
        }
        keys.forEach(::remove)
    }

    private fun remove(key: TraceKey) {
        val removed = entries.remove(key) ?: return
        totalBytes -= removed.bytes
    }
}

private fun snapshotTrace(trace: MockTrace): MockTrace = trace.copy(
    requestPayload = snapshotPayload(trace.requestPayload),
    resultPayload = snapshotPayload(trace.resultPayload),
)

internal fun snapshotPayload(value: Map<String, *>): Map<String, Any?> =
    value.entries.associate { (key, item) -> key to snapshotValue(item) }

private fun snapshotValue(value: Any?): Any? = when (value) {
    null, is String, is Number, is Boolean -> value
    is Map<*, *> -> value.entries.associate { (key, item) -> key.toString() to snapshotValue(item) }
    is Iterable<*> -> value.map(::snapshotValue)
    is Array<*> -> value.map(::snapshotValue)
    else -> value.toString()
}

private fun traceByteSize(trace: MockTrace): Long = jsonByteSize(
    mapOf(
        "sessionId" to trace.sessionId,
        "requestId" to trace.requestId,
        "path" to trace.path,
        "requestPayload" to trace.requestPayload,
        "match" to trace.match.asTraceValue(),
        "delayMs" to trace.delayMs,
        "result" to trace.result.name,
        "resultPayload" to trace.resultPayload,
    ),
)

private fun MockMatchLocation.asTraceValue(): String = when (this) {
    is MockMatchLocation.Branch -> "branch:$index"
    MockMatchLocation.Default -> "default"
    MockMatchLocation.Unregistered -> "unregistered"
}

private fun jsonByteSize(value: Any?): Long = when (value) {
    null -> NULL_BYTES
    is String -> jsonStringByteSize(value)
    is Boolean -> if (value) TRUE_BYTES else FALSE_BYTES
    is Number -> value.toString().length.toLong()
    is Map<*, *> ->
        collectionByteSize(
            value.entries.map { (key, item) ->
                jsonByteSize(key.toString()) + JSON_COLON_BYTES + jsonByteSize(item)
            },
        )
    is Iterable<*> -> collectionByteSize(value.map(::jsonByteSize))
    else -> jsonByteSize(value.toString())
}

private fun collectionByteSize(items: Iterable<Long>): Long {
    var size = JSON_BRACKETS_BYTES
    var first = true
    items.forEach { itemSize ->
        if (!first) size += JSON_COMMA_BYTES
        size += itemSize
        first = false
    }
    return size
}

private fun jsonStringByteSize(value: String): Long {
    var total = STRING_QUOTES_BYTES
    var index = 0
    while (index < value.length) {
        val code = value[index].code
        when {
            value[index] == '"' || value[index] == '\\' -> total += JSON_SHORT_ESCAPE_BYTES
            value[index] in JSON_NAMED_ESCAPE_CHARACTERS -> total += JSON_SHORT_ESCAPE_BYTES
            code < JSON_CONTROL_CHARACTER_LIMIT -> total += JSON_UNICODE_ESCAPE_BYTES
            code <= UTF8_SINGLE_BYTE_LIMIT -> total += 1L
            code <= UTF8_DOUBLE_BYTE_LIMIT -> total += 2L
            code in UTF16_HIGH_SURROGATE_RANGE &&
                index + 1 < value.length &&
                value[index + 1].code in UTF16_LOW_SURROGATE_RANGE -> {
                total += UTF8_FOUR_BYTE_SIZE
                index += 1
            }
            else -> total += UTF8_THREE_BYTE_SIZE
        }
        index += 1
    }
    return total
}

private val PAYLOAD_DROPPED_MARKER: Map<String, Any?> = mapOf(
    "code" to TRACE_PAYLOAD_DROPPED,
)

internal const val MOCK_ROUTE_NOT_FOUND = "MOCK_ROUTE_NOT_FOUND"
internal const val MOCK_ROUTE_NOT_FOUND_MESSAGE = "未注册 Mock 路由"
internal const val MOCK_TRACE_SINK_FAILED = "MOCK_TRACE_SINK_FAILED"
internal const val MOCK_TRACE_LEDGER_RETRY = "MOCK_TRACE_LEDGER_RETRY"
internal const val MOCK_TRACE_METADATA_TOO_LARGE = "MOCK_TRACE_METADATA_TOO_LARGE"
internal const val MOCK_REQUEST_ID_TOO_LONG = "MOCK_REQUEST_ID_TOO_LONG"
internal const val MOCK_CALLBACK_UNBOUND = "MOCK_CALLBACK_UNBOUND"
internal const val MOCK_CALLBACK_HOOK_FAILED = "MOCK_CALLBACK_HOOK_FAILED"
internal const val TRACE_PAYLOAD_DROPPED = "TRACE_PAYLOAD_DROPPED"
internal const val DEFAULT_TRACE_MAX_ENTRIES = 4096
internal const val DEFAULT_TRACE_MAX_BYTES = 16L * 1024L * 1024L

private const val NULL_BYTES = 4L
private const val TRUE_BYTES = 4L
private const val FALSE_BYTES = 5L
private const val STRING_QUOTES_BYTES = 2L
private const val JSON_SHORT_ESCAPE_BYTES = 2L
private const val JSON_UNICODE_ESCAPE_BYTES = 6L
private const val JSON_BRACKETS_BYTES = 2L
private const val JSON_COLON_BYTES = 1L
private const val JSON_COMMA_BYTES = 1L
private const val JSON_CONTROL_CHARACTER_LIMIT = 0x20
private const val UTF8_SINGLE_BYTE_LIMIT = 0x7F
private const val UTF8_DOUBLE_BYTE_LIMIT = 0x7FF
private const val UTF8_THREE_BYTE_SIZE = 3L
private const val UTF8_FOUR_BYTE_SIZE = 4L
private val UTF16_HIGH_SURROGATE_RANGE = 0xD800..0xDBFF
private val UTF16_LOW_SURROGATE_RANGE = 0xDC00..0xDFFF
private val JSON_NAMED_ESCAPE_CHARACTERS = setOf('\b', '\u000C', '\n', '\r', '\t')
